// utils/tags.js
//
// THE REBUILT TAGS, the client's pure half (docs/plans/tags.md §3.1, §5;
// keepr-api docs/SCHEMA.md § Tags): what a tag record is on the wire, how it
// wears its identity, where it may sit in its tree, and the words a refusal
// is said in. No React, no network — the services (services/tags.js), the
// caches (contexts/TagsContext.js, hooks/useCollectionTags.js) and the
// surfaces (components/tags/) all read these, so none of them re-states a rule.
//
// A tag on the wire (kind 'tag'):
//
//   { _id, v: 2, kind: 'tag',
//     owner: { kind: 'user', userId } | { kind: 'collection', collection_id },
//     name, description, color, icon, aliases, parentId, position,
//     restricted, rule, status, createdAt, updatedAt,
//     applyToNewItemsOf,         // a collection tag: card ids, [] when unset (T7)
//     depth?, path? }            // depth and path in the vocabulary only
//
// Two owners, never a third (ruling q6): a PRIVATE tag is one person's, a
// COLLECTION tag is a collection's. Kind 'item' (T2c) is an item's own tag:
// it stores no name, icon or color — those are read live from its item, and
// a surface resolves them through the reader's scope (plan §3.2). Nothing in
// T4a builds one; every helper here answers for it without assuming a name.

import { IDENTITY_HUES } from '../config/brand';
import { normalizeId } from './ids';
import { isAutomaticTag } from './tagRules';

/**
 * The ruled limits (d04, d05), mirrored from keepr-api utils/tagModel.js
 * LIMITS so a form can say them before the API does. The API still judges.
 */
export const TAG_LIMITS = Object.freeze({
	name: 60,
	description: 500,
	aliases: 10,
	depth: 3,
	perOwner: 500,
	// T7 (ruled d22): the cards a restricted tag goes on new items of.
	applyToNewItemsOf: 20,
});

/** Separates a tag from the one above it in a path ("Digestive / Stomach pain"); never inside a name. */
export const TAG_SEPARATOR = '/';

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const tagIdOf = tag => normalizeId(tag?._id) || '';
export const isPrivateTag = tag => tag?.owner?.kind === 'user';
export const isCollectionTag = tag => tag?.owner?.kind === 'collection';
export const isItemTag = tag => tag?.kind === 'item';
export const isRestrictedTag = tag => Boolean(tag?.restricted) && isCollectionTag(tag);
/**
 * "Apply to new items of" (T7, ruled d22): the cards a RESTRICTED collection
 * tag goes on new items of as they are made — card ids, `[]` when unset (the
 * API sends the list on every collection tag).
 */
export const applyToNewItemsOf = tag => (Array.isArray(tag?.applyToNewItemsOf) ? tag.applyToNewItemsOf : [])
	.map(normalizeId)
	.filter(Boolean);
/** The collection that owns a collection tag, or '' for any other. */
export const tagOwnerCollectionId = tag => (isCollectionTag(tag) ? normalizeId(tag.owner.collection_id) || '' : '');
export const tagParentIdOf = tag => normalizeId(tag?.parentId) || null;

/**
 * A tag's color as a CSS color, or null for none. It is an IDENTITY HUE —
 * user data, never theme (docs/platform-standards.md § Identity hues): the
 * API stores a hue key ("emerald") or a hex the picker emitted. A key the
 * palette does not name is no color at all, never a guess.
 */
export function tagColorOf(tag) {
	const raw = typeof tag?.color === 'string' ? tag.color.trim() : '';
	if (!raw) return null;
	if (HEX_COLOR.test(raw)) return raw;
	const hue = IDENTITY_HUES.find(entry => entry.key === raw.toLowerCase());
	return hue ? hue.value : null;
}

/** What RecordBadge / RecordIconTile read: `{ icon, color, name }`. */
export const tagRecordOf = tag => ({
	icon: tag?.icon || undefined,
	color: tagColorOf(tag) || undefined,
	name: tag?.name || '',
});

/**
 * The TAG PAGE's address (`/tags/:tagId`, docs/plans/tags.md §5.2, T5) — for
 * every kind of tag. Null for no tag, or for one this reader cannot open: a
 * tombstone (`null`), a neutral chip (a collection tag whose collection the
 * reader cannot see), or a tag the API named on the row with `openable:
 * false` (keepr-api b74: a restricted tag to a reader of only some items —
 * the name is theirs to see there, the page is not). Each page would be the
 * one 404.
 */
export function tagPagePath(tag) {
	const id = tagIdOf(tag);
	if (!id || tag?.neutral || tag?.openable === false) return null;
	return `/tags/${id}`;
}

/** "Digestive / Stomach pain" — the vocabulary's `path`, else the name alone. */
export function tagPathLabel(tag) {
	if (Array.isArray(tag?.path) && tag.path.length) return tag.path.join(` ${TAG_SEPARATOR} `);
	return tag?.name || '';
}

// ------------------------------------------------------------------ the tree

const foldName = name => `${name || ''}`.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Siblings in their order, the API's (keepr-api tagModel.siblingOrder):
 * `position`, then the name as compared, then the id — so two tags with one
 * position never swap between renders.
 */
export function siblingOrder(a, b) {
	const pa = typeof a?.position === 'number' ? a.position : 0;
	const pb = typeof b?.position === 'number' ? b.position : 0;
	if (pa !== pb) return pa - pb;
	const na = foldName(a?.name);
	const nb = foldName(b?.name);
	if (na !== nb) return na < nb ? -1 : 1;
	return tagIdOf(a) < tagIdOf(b) ? -1 : 1;
}

/**
 * One owner's tags in tree order (pre-order: a tag, then its children), each
 * returned as the tag with `depth` (1 at the top) and `path` recomputed — the
 * vocabulary's own shape, so a list changed on the client (a drag, a create)
 * reads exactly as the next GET would. A tag whose parent is not in the list
 * starts a tree of its own: nothing is ever dropped by the ordering.
 */
export function treeOrder(tags = []) {
	const list = (Array.isArray(tags) ? tags : []).filter(Boolean);
	const byId = new Map(list.map(tag => [tagIdOf(tag), tag]));
	const children = new Map();
	const roots = [];
	list.forEach(tag => {
		const parent = tagParentIdOf(tag);
		if (parent && byId.has(parent) && parent !== tagIdOf(tag)) {
			if (!children.has(parent)) children.set(parent, []);
			children.get(parent).push(tag);
		} else {
			roots.push(tag);
		}
	});
	const out = [];
	const seen = new Set();
	const visit = (tag, depth, path) => {
		const id = tagIdOf(tag);
		if (seen.has(id)) return; // a hand-edited cycle ends here
		seen.add(id);
		const here = [...path, tag.name || ''];
		out.push({ ...tag, depth, path: here });
		(children.get(id) || []).sort(siblingOrder).forEach(child => visit(child, depth + 1, here));
	};
	roots.sort(siblingOrder).forEach(tag => visit(tag, 1, []));
	list.filter(tag => !seen.has(tagIdOf(tag))).sort(siblingOrder).forEach(tag => visit(tag, 1, []));
	return out;
}

/** The owner key of a tag: `c:<collectionId>` or `u:<userId>` (the API's ownerKey, rebuilt from `owner`). */
export function tagOwnerKey(tag) {
	if (isCollectionTag(tag)) return `c:${tagOwnerCollectionId(tag)}`;
	if (isPrivateTag(tag)) return `u:${normalizeId(tag.owner.userId) || ''}`;
	return '';
}

/**
 * The vocabulary split by owner, in the order the API listed the owners (a
 * collection's own tags, then each ancestor's): `[{ key, collectionId, tags }]`,
 * each group in tree order.
 */
export function groupByOwner(tags = []) {
	const groups = new Map();
	(Array.isArray(tags) ? tags : []).forEach(tag => {
		const key = tagOwnerKey(tag);
		if (!groups.has(key)) groups.set(key, { key, collectionId: tagOwnerCollectionId(tag) || null, tags: [] });
		groups.get(key).tags.push(tag);
	});
	return [...groups.values()].map(group => ({ ...group, tags: treeOrder(group.tags) }));
}

/** Direct children of `parentId` (null: the top level), in sibling order. */
export function childrenOf(tags = [], parentId = null) {
	const want = normalizeId(parentId) || null;
	return (tags || []).filter(tag => tagParentIdOf(tag) === want).sort(siblingOrder);
}

/** Every tag beneath `id`, at any depth (a Set of ids). */
export function descendantIdsOf(tags = [], id) {
	const root = normalizeId(id);
	const out = new Set();
	if (!root) return out;
	const queue = [root];
	while (queue.length) {
		const current = queue.shift();
		(tags || []).forEach(tag => {
			const tid = tagIdOf(tag);
			if (tagParentIdOf(tag) === current && !out.has(tid) && tid !== root) {
				out.add(tid);
				queue.push(tid);
			}
		});
	}
	return out;
}

/** 1 for a top-level tag, 2 beneath one, 3 beneath that. */
export function depthOf(tags = [], id) {
	const byId = new Map((tags || []).map(tag => [tagIdOf(tag), tag]));
	let depth = 0;
	let current = byId.get(normalizeId(id));
	const seen = new Set();
	while (current && !seen.has(tagIdOf(current))) {
		seen.add(tagIdOf(current));
		depth += 1;
		current = byId.get(tagParentIdOf(current));
	}
	return depth;
}

/** 1 for a leaf, 2 with children, 3 with grandchildren (the tag counts itself). */
export function subtreeHeight(tags = [], id) {
	const list = tags || [];
	const levelBelow = (frontier, seen) => list
		.filter(tag => frontier.has(tagParentIdOf(tag)) && !seen.has(tagIdOf(tag)))
		.map(tagIdOf);
	let height = 1;
	let frontier = new Set([normalizeId(id)]);
	const seen = new Set(frontier);
	for (let i = 0; frontier.size && i < TAG_LIMITS.depth + 2; i++) {
		const next = levelBelow(frontier, seen);
		if (!next.length) break;
		next.forEach(tid => seen.add(tid));
		height += 1;
		frontier = new Set(next);
	}
	return height;
}

export const hasChildren = (tags = [], id) => (tags || []).some(tag => tagParentIdOf(tag) === normalizeId(id));

/**
 * May `tag` sit under `parentId` (null: the top level)? The API's rules
 * (keepr-api tagUtils placeUnder), said the same way, so the tree never
 * offers a place the API refuses: `{ ok: true }` or `{ ok: false, reason }`.
 *
 * - Anything may go to the top level.
 * - A restricted tag stands alone: no parent, no children (§3.5, ruled d09).
 * - Never under itself or anything beneath it.
 * - Three levels at most, counting everything beneath the tag (ruled d04).
 * - Only under a tag of the same owner (the caller passes one owner's tags).
 */
export function canPlaceUnder(tags = [], tag, parentId) {
	const parent = normalizeId(parentId) || null;
	if (!parent) return { ok: true };
	if (isRestrictedTag(tag)) return { ok: false, reason: 'A restricted tag stands alone: it cannot sit under another tag.' };
	const id = tagIdOf(tag);
	if (parent === id) return { ok: false, reason: 'A tag cannot sit under itself.' };
	const target = (tags || []).find(t => tagIdOf(t) === parent);
	if (!target) return { ok: false, reason: 'That tag is not here any more.' };
	if (isRestrictedTag(target)) return { ok: false, reason: 'A restricted tag stands alone: no tag can sit under it.' };
	if (id && descendantIdsOf(tags, id).has(parent)) return { ok: false, reason: 'A tag cannot sit under a tag that is beneath it.' };
	const height = id ? subtreeHeight(tags, id) : 1;
	if (depthOf(tags, parent) + height > TAG_LIMITS.depth) {
		return { ok: false, reason: `Tags nest ${TAG_LIMITS.depth} levels deep at most.` };
	}
	return { ok: true };
}

/** May a new tag be added under `parentId`? (A new tag is a leaf.) */
export const canAddUnder = (tags, parentId) => canPlaceUnder(tags, { _id: null, restricted: false, owner: { kind: 'collection' } }, parentId);

/** How a name compares (keepr-api tagModel.normalizeKey): NFKC, lower-cased, whitespace collapsed. */
export const tagNameKey = raw => `${raw || ''}`.normalize('NFKC').toLowerCase().normalize('NFKC').replace(/\s+/g, ' ').trim();

/**
 * ALIASES (T9): other names a tag answers to — in filters, search, and what
 * an assistant types (keepr-api tagModel.cleanAliases). The rules, said the
 * way the API says them: each an ordinary tag name (no "/", at most 60
 * characters, not empty), at most 10, duplicates and the tag's own name left
 * out quietly (they add nothing). → `{ aliases, error }`: the cleaned list,
 * and the first rule the list breaks (null when it breaks none — `aliases`
 * is then what the API will store).
 */
export function cleanAliases(list = [], ownName = '') {
	const seen = new Set([tagNameKey(ownName)]);
	const aliases = [];
	let error = null;
	for (const raw of list || []) {
		const name = `${raw || ''}`.replace(/\s+/g, ' ').trim();
		if (!name) continue;
		if (name.normalize('NFKC').includes(TAG_SEPARATOR)) {
			error = error || 'An alias cannot contain "/" — it separates a tag from the tag above it.';
			continue;
		}
		if ([...name].length > TAG_LIMITS.name) {
			error = error || `An alias is at most ${TAG_LIMITS.name} characters.`;
			continue;
		}
		const key = tagNameKey(name);
		if (seen.has(key)) continue;
		seen.add(key);
		aliases.push(name);
	}
	if (aliases.length > TAG_LIMITS.aliases) error = error || `A tag has at most ${TAG_LIMITS.aliases} aliases.`;
	return { aliases, error };
}

/** An automatic tag (T6a): one a rule applies. */
// One rule, in utils/tagRules.js (T6a): a tag with a rule behind it. Re-exported
// for T9's merge checks and its callers.
export { isAutomaticTag };

/**
 * May `tag` be merged into `into` (T9, keepr-api tagMerge)? The API's rules,
 * said the same way: `{ ok: true }` or `{ ok: false, reason }`. Same owner
 * (the caller passes one owner's tags), neither restricted nor automatic,
 * never itself or a tag beneath it.
 */
export function canMergeInto(tags = [], tag, into) {
	const id = tagIdOf(tag);
	const target = normalizeId(tagIdOf(into) || into);
	if (!target || target === id) return { ok: false, reason: 'A tag can\'t be merged into itself.' };
	const other = (tags || []).find(t => tagIdOf(t) === target);
	if (!other) return { ok: false, reason: 'That tag is not here any more.' };
	if (isRestrictedTag(tag) || isRestrictedTag(other)) return { ok: false, reason: 'Restricted tags can\'t be merged: they decide who can see what.' };
	if (isAutomaticTag(tag) || isAutomaticTag(other)) return { ok: false, reason: 'An automatic tag can\'t be merged. Stop applying it by rule first.' };
	if (descendantIdsOf(tags, id).has(target)) return { ok: false, reason: 'It is under this tag. Move it out first.' };
	return { ok: true };
}

/** The tags "Merge into…" offers `tag`, in tree order, each with why not when it can't (never `tag` itself). */
export function mergeTargets(tags = [], tag) {
	return treeOrder(tags)
		.filter(candidate => tagIdOf(candidate) !== tagIdOf(tag))
		.map(candidate => {
			const verdict = canMergeInto(tags, tag, candidate);
			return { id: tagIdOf(candidate), tag: candidate, depth: candidate.depth, disabled: !verdict.ok, reason: verdict.ok ? null : verdict.reason };
		});
}

/**
 * The places "Move to…" offers `tag`, in tree order: the top level first,
 * then every tag of the same owner, each `{ id, tag, depth, disabled,
 * reason, current }`. A place the tag cannot go is listed but disabled with
 * its reason, so the list explains itself rather than going quiet.
 */
export function moveTargets(tags = [], tag) {
	const current = tagParentIdOf(tag);
	const out = [{ id: null, tag: null, depth: 0, disabled: current === null, reason: current === null ? 'It is there now.' : null, current: current === null }];
	treeOrder(tags).forEach(candidate => {
		const id = tagIdOf(candidate);
		if (id === tagIdOf(tag)) return;
		const verdict = canPlaceUnder(tags, tag, id);
		const here = current === id;
		out.push({
			id,
			tag: candidate,
			depth: candidate.depth,
			disabled: here || !verdict.ok,
			reason: here ? 'It is there now.' : (verdict.ok ? null : verdict.reason),
			current: here,
		});
	});
	return out;
}

/** A position after every sibling in `siblings` (already in order). */
export function positionAfter(siblings = []) {
	const last = siblings.length ? siblings[siblings.length - 1] : null;
	return last && typeof last.position === 'number' ? Math.floor(last.position) + 1 : 0;
}

/**
 * A position strictly between two siblings (either may be missing), or null
 * when there is no room — two siblings sharing a position, which only a
 * renumbering can separate (`renumberedPositions`).
 */
export function positionBetween(before, after) {
	const b = before && typeof before.position === 'number' ? before.position : null;
	const a = after && typeof after.position === 'number' ? after.position : null;
	if (b === null && a === null) return 0;
	if (b === null) return a - 1;
	if (a === null) return Math.floor(b) + 1;
	if (!(b < a)) return null;
	const mid = (b + a) / 2;
	return mid > b && mid < a ? mid : null;
}

/**
 * Where a tag lands when it is dropped in the flattened tree:
 *
 *   A DROPPED TAG TAKES THE PLACE OF THE ROW IT LANDS ABOVE — the same parent
 *   as that row, just before it. Dropped after the last row, it goes to the
 *   end of the top level.
 *
 * One rule a person can predict: to put a tag first under "Digestive", drop
 * it above Digestive's first tag; to move it out to the top level, drop it
 * above a top-level tag. (Anything else — "under this leaf" — is Move to….)
 *
 * `order` is the flattened ids after the drop (SortableList's answer), with
 * the dragged tag at its new index; its own subtree rows are skipped when
 * looking for the row below, since they travel with it. Returns
 * `{ parentId, position, renumber? }`, `{ refused: reason }`, or null when
 * nothing moves.
 */
export function placementForDrop(tags = [], order = [], activeId) {
	const active = (tags || []).find(tag => tagIdOf(tag) === activeId);
	if (!active) return null;
	const at = order.indexOf(activeId);
	if (at < 0) return null;
	const beneath = descendantIdsOf(tags, activeId);
	const belowId = order.slice(at + 1).find(id => !beneath.has(id)) || null;
	const below = belowId ? (tags || []).find(tag => tagIdOf(tag) === belowId) : null;
	const parentId = below ? tagParentIdOf(below) : null;
	const verdict = canPlaceUnder(tags, active, parentId);
	if (!verdict.ok) return { refused: verdict.reason };
	const siblings = childrenOf(tags, parentId).filter(tag => tagIdOf(tag) !== activeId);
	let position;
	if (below) {
		const index = siblings.findIndex(tag => tagIdOf(tag) === belowId);
		position = positionBetween(index > 0 ? siblings[index - 1] : null, siblings[index]);
		if (position === null) {
			return { parentId, renumber: [...siblings.slice(0, index).map(tagIdOf), activeId, ...siblings.slice(index).map(tagIdOf)] };
		}
	} else {
		position = positionAfter(siblings);
	}
	if (parentId === tagParentIdOf(active) && position === active.position) return null;
	// Nothing moved when the tag keeps its place among the same siblings.
	if (parentId === tagParentIdOf(active)) {
		const before = childrenOf(tags, parentId).map(tagIdOf);
		const after = [...siblings.map(tagIdOf)];
		const index = below ? after.indexOf(belowId) : after.length;
		after.splice(index, 0, activeId);
		if (before.join() === after.join()) return null;
	}
	return { parentId, position };
}

/**
 * "Move up" / "Move down" — the drag's menu twin (docs/platform-standards.md
 * § Drag to reorder): one place among the tag's own siblings.
 */
export function placementForStep(tags = [], activeId, delta) {
	const active = (tags || []).find(tag => tagIdOf(tag) === activeId);
	if (!active) return null;
	const parentId = tagParentIdOf(active);
	const siblings = childrenOf(tags, parentId);
	const index = siblings.findIndex(tag => tagIdOf(tag) === activeId);
	const target = index + delta;
	if (index < 0 || target < 0 || target >= siblings.length) return null;
	const others = siblings.filter(tag => tagIdOf(tag) !== activeId);
	const position = positionBetween(target > 0 ? others[target - 1] : null, others[target] || null);
	if (position === null) {
		const ids = others.map(tagIdOf);
		ids.splice(target, 0, activeId);
		return { parentId, renumber: ids };
	}
	return { parentId, position };
}

/** Whole-number positions for `ids` in order: only the ones that change, `[{ id, position }]`. */
export function renumberedPositions(tags = [], ids = []) {
	const byId = new Map((tags || []).map(tag => [tagIdOf(tag), tag]));
	return ids
		.map((id, index) => ({ id, position: index }))
		.filter(({ id, position }) => byId.get(id)?.position !== position);
}

/** The list with `patch` applied to the tag `id` (depth and path recomputed per owner). */
export function withTagPatched(tags = [], id, patch) {
	return reorderByOwner((tags || []).map(tag => (tagIdOf(tag) === id ? { ...tag, ...patch } : tag)));
}

/** The list with `tag` replacing its old self, or added. */
export function withTag(tags = [], tag) {
	const id = tagIdOf(tag);
	const found = (tags || []).some(t => tagIdOf(t) === id);
	return reorderByOwner(found ? tags.map(t => (tagIdOf(t) === id ? { ...t, ...tag } : t)) : [...(tags || []), tag]);
}

/** The list without the tag `id`. */
export const withoutTag = (tags = [], id) => (tags || []).filter(tag => tagIdOf(tag) !== id);

/** Tree order within each owner, owners kept in their listed order. */
export function reorderByOwner(tags = []) {
	return groupByOwner(tags).flatMap(group => group.tags);
}

// ------------------------------------------------------------------ names

/** id → the tag, for resolving ids a History row or an item carries. */
export function tagsById(tags = []) {
	const map = new Map();
	(tags || []).forEach(tag => { const id = tagIdOf(tag); if (id) map.set(id, tag); });
	return map;
}

/**
 * The words a stored tag id reads as, resolved from a vocabulary the reader
 * holds: its name, or "a deleted tag" when the vocabulary does not have it
 * (deleted since — or, until item tags and `tagTitles` arrive, one the
 * reader's vocabulary cannot name).
 */
export const DELETED_TAG_LABEL = 'a deleted tag';
export function tagNameFor(id, byId) {
	const tag = byId && typeof byId.get === 'function' ? byId.get(normalizeId(id)) : null;
	return tag?.name || DELETED_TAG_LABEL;
}

// ------------------------------------------------------------------ refusals

// The holders an access-shaping `tag_in_use` names (keepr-api
// utils/tagQueryScan.js, the `access` class), in plain words.
const HOLDER_WORDS = {
	filteredAccessLevel: 'a filtered access level',
	collectionLink: 'a collection link',
	linkRequest: 'a link request',
	dynamicGroup: 'a group built from items',
	publicLinkView: 'a public link',
};

/** "a filtered access level, a public link" — what an access `tag_in_use` names. */
export function describeTagHolders(references = []) {
	const words = [...new Set((references || []).map(ref => HOLDER_WORDS[ref?.holder] || 'an access setting'))];
	return words.join(', ');
}

// ------------------------------------------------------------------ restricted tags in access (T7)

const sharingPath = collectionId => `/collections/${collectionId}/form?tab=sharing`;

/**
 * One setting an access `tag_in_use` names (T7: keepr-api tagUtils
 * describeAccessReferences — `{ holder, id, collectionId, collectionName,
 * name, principalType? }`), as a person reads it — "Alex Rivera’s access
 * level", "the public link “Agenda”" — with where it lives and the door to
 * where it is changed: the collection's Sharing tab (a link or a link
 * request: its Linked collections section).
 */
export function tagHolderReference(ref) {
	const name = typeof ref?.name === 'string' && ref.name.trim() ? ref.name.trim() : null;
	const collectionId = normalizeId(ref?.collectionId) || null;
	let text;
	switch (ref?.holder) {
		case 'filteredAccessLevel':
			if (ref.principalType === 'group') text = name ? `the ${name} group’s access level` : 'a group’s access level';
			else text = name ? `${name}’s access level` : 'someone’s access level';
			break;
		case 'collectionLink':
			text = name ? `the link to ${name}` : 'a collection link';
			break;
		case 'linkRequest':
			text = name ? `the link request from ${name}` : 'a link request';
			break;
		case 'dynamicGroup':
			text = name ? `the group “${name}”` : 'a group built from items';
			break;
		case 'publicLinkView':
			text = name ? `the public link “${name}”` : 'a public link';
			break;
		default:
			text = 'an access setting';
	}
	const linked = ref?.holder === 'collectionLink' || ref?.holder === 'linkRequest';
	return {
		key: `${ref?.holder || 'setting'}:${normalizeId(ref?.id) || ref?.id || ''}`,
		text,
		where: typeof ref?.collectionName === 'string' && ref.collectionName.trim() ? ref.collectionName.trim() : null,
		to: collectionId ? `${sharingPath(collectionId)}${linked ? '#linked-collections' : ''}` : null,
	};
}

/** "a", "a and b", "a, b, and c" — a list said in a sentence. */
export function joinWords(words = []) {
	const list = (words || []).filter(Boolean);
	if (list.length <= 1) return list[0] || '';
	if (list.length === 2) return `${list[0]} and ${list[1]}`;
	return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

/**
 * A `tag_in_use` refused because ACCESS SETTINGS name the tag (409, `reason:
 * 'access'`; T7) — what uses it, in plain words, or null for any other
 * refusal (or one from an API that names nothing). `references` are the
 * settings the caller manages (each named, with its door); `hidden` counts
 * the ones they can't see, never named.
 *
 *   { lead, references: [{ key, text, where, to }], hidden, more, tail }
 *
 * `tagName` names the tag in the lead ("“Board only” decides who can see
 * what."); without it the lead says "It" — for a line that already named it.
 */
export function describeTagInUse(error, { tagName = null } = {}) {
	const data = error?.response?.data || {};
	if (data.code !== 'tag_in_use' || data.reason !== 'access') return null;
	const refs = (Array.isArray(data.references) ? data.references : []).filter(ref => ref && ref.collectionId);
	const hidden = Math.max(0, Number(data.hidden) || 0);
	if (!refs.length && !hidden) return null;
	const references = refs.map(tagHolderReference);
	const more = hidden
		? (references.length ? `${hidden} more you can’t see` : `${hidden === 1 ? 'an access setting' : `${hidden} access settings`} you can’t see`)
		: null;
	let tail;
	if (!references.length) tail = `Someone who manages ${hidden === 1 ? 'it' : 'them'} has to change ${hidden === 1 ? 'it' : 'them'} first.`;
	else tail = references.length + hidden === 1 ? 'Change it first.' : 'Change them first.';
	return {
		lead: tagName ? `“${tagName}” decides who can see what.` : 'It decides who can see what.',
		references,
		hidden,
		more,
		tail,
	};
}

/**
 * A `tag_in_use` refused because STRICT LOOKUPS name the tag (409, `reason:
 * 'lookup'`; the owner's ruling b80): a lookup that only offers items with
 * the tag would refuse every new value once it is gone, so its filter is
 * edited first. Null for any other refusal. `references` are the lookups the
 * caller can open (keepr-api tagUtils inUseByLookups — `{ holder, id,
 * collectionId, collectionName, name, icon, color, element, label, link }`),
 * each drawn as the record badge of its card or element set; `hidden` counts
 * the ones they cannot open, never named.
 *
 *   { lead, tail, references: [{ key, kind, record, title, to, collection }],
 *     hidden, more, total }
 */
export function describeTagLookups(error) {
	const data = error?.response?.data || {};
	if (data.code !== 'tag_in_use' || data.reason !== 'lookup') return null;
	const refs = (Array.isArray(data.references) ? data.references : []).filter(ref => ref && ref.id);
	const hidden = Math.max(0, Number(data.hidden) || 0);
	if (!refs.length && !hidden) return null;
	const references = refs.map(ref => {
		const id = normalizeId(ref.id) || ref.id;
		const set = ref.holder === 'elementSetLookupFilter';
		const title = typeof ref.name === 'string' && ref.name.trim() ? ref.name.trim() : (set ? 'Untitled element set' : 'Untitled card');
		const collectionId = normalizeId(ref.collectionId) || null;
		const collectionName = typeof ref.collectionName === 'string' && ref.collectionName.trim() ? ref.collectionName.trim() : null;
		return {
			key: `${ref.holder}:${id}`,
			kind: set ? 'elementSet' : 'card',
			record: { name: title, icon: ref.icon || null, color: ref.color || null },
			title,
			to: typeof ref.link === 'string' && ref.link.startsWith('/') ? ref.link : (set ? `/element-sets/${id}` : `/cards/${id}`),
			collection: collectionId && collectionName ? { _id: collectionId, name: collectionName } : null,
		};
	});
	const total = references.length + hidden;
	const lead = total === 1
		? 'This tag is used by a lookup that only offers items with it.'
		: `This tag is used by ${total} lookups that only offer items with it.`;
	let tail;
	if (!references.length) tail = `Someone who manages ${hidden === 1 ? 'it' : 'them'} has to edit ${hidden === 1 ? 'its filter' : 'their filters'} first.`;
	else tail = total === 1 ? 'Edit its filter first:' : 'Edit their filters first:';
	let more = null;
	if (hidden) {
		if (references.length) more = hidden === 1 ? '1 lookup you cannot open' : `${hidden} lookups you cannot open`;
		else more = hidden === 1 ? 'It is a lookup you cannot open.' : 'They are lookups you cannot open.';
	}
	return { lead, tail, references, hidden, more, total };
}

/** `describeTagLookups`' parts as one plain sentence (no badges). */
export function tagLookupsSentence(parts) {
	if (!parts) return '';
	if (!parts.references.length) return `${parts.lead} ${parts.more} ${parts.tail}`;
	const words = parts.references.map(ref => ref.title);
	if (parts.more) words.push(parts.more);
	return `${parts.lead} ${parts.tail.replace(/:$/, '')}: ${joinWords(words)}.`;
}

/**
 * The warning "Apply to new items of" gives when it WIDENS (keepr-api b75,
 * `shows_new_items`, from the change's dry run): the access settings that
 * show items with the tag will show every new item of the cards added. Null
 * when the answer carries none. The settings read as `tag_in_use`'s do
 * (tagHolderReference: each one the caller manages, named, with its door;
 * `hidden` the rest, counted):
 *
 *   { cardIds, references, hidden, more, total }
 *
 * `more` is "2 more you can’t see" (or, with none named, "2 access settings
 * you can’t see"); `total` counts them all.
 */
export function describeNewItemsWarning(warnings) {
	const warning = (Array.isArray(warnings) ? warnings : []).find(w => w && w.code === 'shows_new_items');
	if (!warning) return null;
	const refs = (Array.isArray(warning.references) ? warning.references : []).filter(ref => ref && ref.collectionId);
	const hidden = Math.max(0, Number(warning.hidden) || 0);
	if (!refs.length && !hidden) return null;
	const references = refs.map(tagHolderReference);
	const more = hidden
		? (references.length ? `${hidden} more you can’t see` : `${hidden === 1 ? 'an access setting' : `${hidden} access settings`} you can’t see`)
		: null;
	return {
		cardIds: (Array.isArray(warning.cardIds) ? warning.cardIds : []).map(normalizeId).filter(Boolean),
		references,
		hidden,
		more,
		total: references.length + hidden,
	};
}

/** `describeTagInUse`'s parts as one plain sentence (no links). */
export function tagInUseSentence(parts) {
	if (!parts) return '';
	const words = parts.references.map(ref => (ref.where ? `${ref.text} (${ref.where})` : ref.text));
	if (parts.more) words.push(parts.more);
	return `${parts.lead} It’s used by ${joinWords(words)}. ${parts.tail}`;
}

/**
 * The `negated_restricted_tag` warnings an access writer answers with (T7,
 * ruled d22: a saved access query that leaves items out by a restricted tag
 * — the gap before the tag is on a new item): the API's own sentences, as
 * given, once each. '' when there are none.
 */
export function negatedTagWarningText(warnings) {
	const messages = (Array.isArray(warnings) ? warnings : [])
		.filter(warning => warning && warning.code === 'negated_restricted_tag' && typeof warning.message === 'string' && warning.message.trim())
		.map(warning => warning.message.trim());
	return [...new Set(messages)].join(' ');
}

/**
 * A tag write's refusal as the sentence a person reads. The API says each in
 * plain words already (keepr-api tagUtils — "A tag named "Urgent" is already
 * there…"), so its message is kept; a code without one gets ours, and an
 * access-shaping `tag_in_use` also says which kinds of settings hold it.
 */
const FALLBACKS = {
	tag_name_taken: 'A tag beside it already has that name. Tags beside each other need different names.',
	tag_limit_reached: `This list already has ${TAG_LIMITS.perOwner} tags, the most it can hold. Delete some first.`,
	tag_has_children: 'This tag has tags under it. Move or delete them first.',
	tag_in_use: 'This tag is in use, so it cannot change that way.',
	invalid_parent: 'A tag cannot go there.',
	collection_archived: 'This collection is archived, so its tags cannot change.',
	unconfined_key_required: 'Use keepr itself to do this.',
	not_deleted: 'This tag is not deleted.',
	parent_deleted: 'The tag this one sat under is deleted. Restore that tag first.',
	legacy_tags_present: 'Tags aren’t available right now. Try again in a little while.',
	history_unavailable: 'The change history could not be written, so nothing changed. Try again in a moment.',
	// T9: a merge's refusals (keepr-api tagMerge) — the API's sentence is kept
	// when it sends one.
	invalid_merge: 'It can\'t be merged into that tag.',
	tag_restricted: 'Restricted tags can\'t be merged: they decide who can see what.',
	tag_has_rule: 'An automatic tag can\'t be merged. Stop applying it by rule first.',
	tag_merged: 'This tag was merged into another tag, and a merge can\'t be undone.',
	parent_full: 'A tag holds at most 200 item tags.',
	personal_tag_full: 'A private tag holds at most 2,000 items and 50 collections, and together these would hold more.',
	// Automatic tags (T6a).
	applied_by_rule: 'Items still carry this tag from its rule, so it can’t be restricted yet. Choose Stop applying by rule… in its ⋯ to turn them into hand tags, then restrict it.',
};

export function tagRefusal(error, fallback = 'The tag could not be saved. Please try again.', { tags = null, tagName = null } = {}) {
	if (!error?.response) return "We couldn't reach the server. Check your connection and try again.";
	const status = error.response.status;
	const data = error.response.data || {};
	const code = data.code || null;
	if (status === 404) return 'This tag is not there any more. It may have been deleted.';
	if (status === 403 && !code) return 'Only managers of this collection can change its tags.';
	let text = typeof data.message === 'string' && data.message ? data.message : (FALLBACKS[code] || fallback);
	// T7: access settings name the tag — say which, in plain words (the
	// surfaces draw each as a link: components/tags/TagRefusal.js).
	const inUse = describeTagInUse(error, { tagName });
	if (inUse) return tagInUseSentence(inUse);
	// b80: strict lookups name the tag — which ones, in plain words (the
	// tag manager draws each as a badge: components/tags/TagRefusal.js).
	const lookups = describeTagLookups(error);
	if (lookups) return tagLookupsSentence(lookups);
	if (code === 'tag_in_use' && data.reason === 'access' && Array.isArray(data.references) && data.references.length) {
		text = `${text} (${describeTagHolders(data.references)})`;
	}
	// T6a: items still carry the tag by rule — say how many, and the way out
	// (the API's sentence names no menu entry).
	if (code === 'applied_by_rule') {
		const count = Number(data.count) || 0;
		return count
			? `${count === 1 ? 'An item still carries' : `${count} items still carry`} this tag from its rule, so it can’t be restricted yet. Choose Stop applying by rule… in its ⋯ to turn ${count === 1 ? 'it into a hand tag' : 'them into hand tags'}, then restrict it.`
			: FALLBACKS.applied_by_rule;
	}
	// T6a: a move that would make a rule read its own tag — named, when the
	// caller holds the vocabulary (`tagId` is the tag whose rule it is).
	if (code === 'invalid_parent' && data.reason === 'rule_reads_tag') {
		const ruled = data.tagId ? tagsById(Array.isArray(tags) ? tags : []).get(normalizeId(data.tagId)) : null;
		return ruled
			? `The rule of “${ruled.name}” reads the tag you are moving it under, or one above it, so there it would depend on itself. Change that rule first.`
			: text;
	}
	return text;
}

// ------------------------------------------------------------------ private tags on collections (b53)

/**
 * The most collections one private tag goes on (ruled d05; keepr-api
 * tagApplicationUtils CAPS.collection — `personal_tag_full` past it).
 */
export const PRIVATE_TAG_COLLECTIONS_LIMIT = 50;

const groupsOf = groups => (Array.isArray(groups) ? groups : []).filter(group => group && group.tag && Array.isArray(group.collections));

/**
 * Which of my private tags are on a collection, read from Home's
 * `collectionGroups` (`[{ tag, collections: [identity] }]`, keepr-api
 * homeUtils.collectionGroupsOf — the only read of the private store by
 * collection): the tag ids, in the groups' order (my tags' tree order).
 */
export function privateTagIdsOnCollection(groups, collectionId) {
	const id = normalizeId(collectionId);
	if (!id) return [];
	return groupsOf(groups)
		.filter(group => group.collections.some(collection => normalizeId(collection?._id) === id))
		.map(group => tagIdOf(group.tag))
		.filter(Boolean);
}

/** The collections a private tag is on, from the same groups: their identities, by name. */
export function collectionsOfPrivateTag(groups, tagId) {
	const id = normalizeId(tagId);
	const group = id ? groupsOf(groups).find(entry => tagIdOf(entry.tag) === id) : null;
	return group ? group.collections.filter(collection => normalizeId(collection?._id)) : [];
}

const COLLECTION_FAILURE_WORDS = {
	unknown_tag: 'That private tag isn’t there any more.',
	not_found: 'That collection can’t be found, or you can no longer read it.',
	personal_tag_full: `A private tag goes on ${PRIVATE_TAG_COLLECTIONS_LIMIT} collections at most, and this one is on that many. Take it off one first.`,
};

/**
 * A refused private tag on a collection (`PUT` / `DELETE
 * /api/tag-applications` with `targetType: 'collection'`) as one sentence:
 * the 409's first failure (`failures: [{ index, code }]`) or its code, in
 * plain words.
 */
export function privateCollectionTagRefusal(error) {
	if (!error?.response) return "We couldn't reach the server. Check your connection and try again.";
	const data = error.response.data || {};
	const failures = Array.isArray(data.failures) ? data.failures : [];
	const code = (failures[0] && failures[0].code) || data.code || null;
	if (COLLECTION_FAILURE_WORDS[code]) return COLLECTION_FAILURE_WORDS[code];
	if (error.response.status === 429) return 'That is a lot of changes in a short time. Wait a little, then try again.';
	return (typeof data.message === 'string' && data.message) || 'The private tag could not be changed. Please try again.';
}
